/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { useWalkthrough } from '../../contexts/WalkthroughContext';
import { Check, ExternalLink, AlertCircle } from 'lucide-react';

export function PrerequisitesDialog() {
  const {
    state,
    hidePrerequisitesDialog,
    updatePrerequisite,
    completePrerequisites,
    skipWalkthrough,
  } = useWalkthrough();

  const cursorCheck = state.prerequisites.find(p => p.id === 'cursor');
  const claudeCodeCheck = state.prerequisites.find(p => p.id === 'claude-code');
  const cursorAgentCheck = state.prerequisites.find(p => p.id === 'cursor-agent');

  const isCursorCompleted = cursorCheck?.isCompleted ?? false;
  const hasCodeAgent =
    (claudeCodeCheck?.isCompleted ?? false) ||
    (cursorAgentCheck?.isCompleted ?? false);

  // User can always proceed - these are suggestions, not requirements
  const hasAllSuggested = isCursorCompleted && hasCodeAgent;

  return (
    <Dialog open={state.showPrerequisites} onOpenChange={(open) => !open && hidePrerequisitesDialog()}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <span className="text-2xl">🚀</span>
            Welcome to Velocity!
          </DialogTitle>
          <DialogDescription className="text-base">
            For the best experience with AI-powered development, we strongly recommend installing these tools. You can still use Velocity without them, but some features will be limited.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Cursor IDE - Strongly Recommended */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm text-slate-700 dark:text-slate-300 flex items-center gap-2">
              Strongly Recommended
            </h4>
            
            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                isCursorCompleted
                  ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800'
                  : 'bg-slate-50 border-slate-200 hover:bg-slate-100 dark:bg-slate-900 dark:border-slate-700'
              }`}
            >
              <Checkbox
                id="cursor"
                checked={isCursorCompleted}
                onCheckedChange={(checked) => updatePrerequisite('cursor', !!checked)}
                className="mt-0.5"
              />
              <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Cursor IDE</span>
                  {isCursorCompleted && (
                    <Check className="h-4 w-4 text-green-600" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  AI-first code editor for agentic code generation.
                </p>
                <a
                  href="https://cursor.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  Download from cursor.com
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </label>
          </div>

          {/* Coding Agent - At least one recommended */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm text-slate-700 dark:text-slate-300 flex items-center gap-2">
              Coding Agent
              <span className="text-sm font-normal text-muted-foreground">(at least one recommended)</span>
            </h4>
            
            {/* Claude Code */}
            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                claudeCodeCheck?.isCompleted
                  ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800'
                  : 'bg-slate-50 border-slate-200 hover:bg-slate-100 dark:bg-slate-900 dark:border-slate-700'
              }`}
            >
              <Checkbox
                id="claude-code"
                checked={claudeCodeCheck?.isCompleted ?? false}
                onCheckedChange={(checked) => updatePrerequisite('claude-code', !!checked)}
                className="mt-0.5"
              />
              <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Claude Code</span>
                  {claudeCodeCheck?.isCompleted && (
                    <Check className="h-4 w-4 text-green-600" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  Anthropic's agentic coding assistant. Recommended for best results.
                </p>
                <a
                  href="https://docs.anthropic.com/en/docs/claude-code/overview"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  Download Claude Code
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </label>

            {/* Cursor CLI */}
            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                cursorAgentCheck?.isCompleted
                  ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800'
                  : 'bg-slate-50 border-slate-200 hover:bg-slate-100 dark:bg-slate-900 dark:border-slate-700'
              }`}
            >
              <Checkbox
                id="cursor-agent"
                checked={cursorAgentCheck?.isCompleted ?? false}
                onCheckedChange={(checked) => updatePrerequisite('cursor-agent', !!checked)}
                className="mt-0.5"
              />
              <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Cursor CLI</span>
                  {cursorAgentCheck?.isCompleted && (
                    <Check className="h-4 w-4 text-green-600" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  Command-line interface for Cursor IDE. Install from Cursor's Settings menu.
                </p>
                <a
                  href="https://docs.cursor.com/context/codebase-indexing#cursor-cli"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  Learn how to install Cursor CLI
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </label>
          </div>

          {/* Status Message */}
          {!hasAllSuggested && (
            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950 text-blue-800 dark:text-blue-200 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>
                For full AI-powered development capabilities, we recommend installing Cursor IDE and at least one coding agent. You can proceed without them, but some features won't be available.
              </span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={skipWalkthrough}
            className="text-muted-foreground"
          >
            Skip Tour
          </Button>
          <Button
            onClick={completePrerequisites}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Start Guided Tour
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

